import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useWallet } from '@solana/wallet-adapter-react';
import { useWalletModal } from '@solana/wallet-adapter-react-ui';
import { Gamepad2, ShoppingBag, Trophy, Wallet, Zap, Calendar, Users, Eye, Volume2, User, Backpack, Bomb, BookOpen, Crown } from 'lucide-react';
import { Button } from './ui/button';
import SoundSettings from './SoundSettings';
import BackendStatusBadge from './BackendStatusBadge';
import InstallAppButton from './InstallAppButton';
import EnableNotificationsButton from './EnableNotificationsButton';
import { preloadAllBackgrounds } from '../game/constants';

const MENU_VIDEO_URL = "/menu_background.mp4";

const MainMenu = ({ 
  onPlay, 
  onShop, 
  onLeaderboard,
  onChallenges,
  onMultiplayer,
  onTournaments,
  onSpectate,
  onBossRoster,
  onBestiary,
  onProfile,
  onLoadout,
  tokenBalance = 0,
  selectedSkin,
  selectedWeapon,
  bringGrenade
}) => {
  const { connected, publicKey, disconnect, wallet } = useWallet();
  const { setVisible } = useWalletModal();
  const videoRef = useRef(null);
  const [videoOpacity, setVideoOpacity] = useState(0);
  const [isVideoLoaded, setIsVideoLoaded] = useState(false);
  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [showSoundSettings, setShowSoundSettings] = useState(false);
  const fadeTimeoutRef = useRef(null);
  
  // Attempt to play video with iOS compatibility
  const tryPlayVideo = useCallback(() => {
    if (!videoRef.current) return;
    
    const video = videoRef.current;
    video.muted = true; // Ensure muted for autoplay
    
    const playPromise = video.play();
    if (playPromise !== undefined) {
      playPromise
        .then(() => {
          console.log('Video playing successfully');
          setVideoOpacity(1);
        })
        .catch((error) => {
          console.log('Video autoplay prevented:', error);
          // On iOS, video may need user interaction - it will play on first touch
        });
    }
  }, []);
  
  // Handle video fade cycle: fade in -> play -> fade out -> 3 second pause -> repeat
  const handleVideoEnded = useCallback(() => {
    // Fade out over 1 second
    setVideoOpacity(0);
    
    // Clear any existing timeout
    if (fadeTimeoutRef.current) {
      clearTimeout(fadeTimeoutRef.current);
    }
    
    // Wait 3 seconds, then restart and fade back in
    fadeTimeoutRef.current = setTimeout(() => {
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
        tryPlayVideo();
      }
    }, 3000);
  }, [tryPlayVideo]);
  
  // Handle user interaction to enable video on iOS
  const handleUserInteraction = useCallback(() => {
    if (!hasUserInteracted) {
      setHasUserInteracted(true);
      tryPlayVideo();
    }
  }, [hasUserInteracted, tryPlayVideo]);
  
  // Start video when loaded
  useEffect(() => {
    if (isVideoLoaded && videoRef.current) {
      // Set video properties for iOS
      const video = videoRef.current;
      video.setAttribute('playsinline', '');
      video.setAttribute('webkit-playsinline', '');
      video.muted = true;
      video.loop = false; // We handle looping manually with fade
      
      tryPlayVideo();
    }
  }, [isVideoLoaded, tryPlayVideo]);
  
  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (fadeTimeoutRef.current) {
        clearTimeout(fadeTimeoutRef.current);
      }
    };
  }, []);

  // Preload all 20 level sky JPEGs during main-menu idle so entering the game
  // (or advancing between levels) never shows the CSS-gradient fallback flash.
  // Runs once on mount; `loadBackgroundImage` dedupes so re-mounts are free.
  useEffect(() => {
    preloadAllBackgrounds();
  }, []);
  
  // Listen for any user interaction to enable video on iOS
  useEffect(() => {
    const handleTouch = () => handleUserInteraction();
    const handleClick = () => handleUserInteraction();
    
    document.addEventListener('touchstart', handleTouch, { once: true, passive: true });
    document.addEventListener('click', handleClick, { once: true });
    
    return () => {
      document.removeEventListener('touchstart', handleTouch);
      document.removeEventListener('click', handleClick);
    };
  }, [handleUserInteraction]);

  return (
    <div 
      className="min-h-screen flex flex-col items-center justify-center p-4 relative overflow-hidden"
      onClick={handleUserInteraction}
      onTouchStart={handleUserInteraction}
    >
      {/* Background Video Layer */}
      <div className="absolute inset-0 z-0">
        <video
          ref={videoRef}
          src={MENU_VIDEO_URL}
          className="absolute inset-0 w-full h-full object-cover transition-opacity duration-1000"
          style={{ opacity: videoOpacity }}
          muted
          playsInline
          webkit-playsinline=""
          preload="auto"
          onLoadedData={() => setIsVideoLoaded(true)}
          onCanPlay={() => {
            if (!isVideoLoaded) setIsVideoLoaded(true);
          }}
          onEnded={handleVideoEnded}
          onError={(e) => console.log('Video error:', e)}
          data-testid="menu-background-video"
        />
        {/* Dark overlay for better text readability */}
        <div 
          className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/50 to-black/80 transition-opacity duration-1000"
          style={{ opacity: videoOpacity > 0 ? 0.7 : 0 }}
        />
      </div>
      
      {/* Animated Background Particles */}
      <div className="particles-bg z-10">
        {[...Array(20)].map((_, i) => (
          <div 
            key={i}
            className="particle"
            style={{
              left: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 10}s`,
              animationDuration: `${10 + Math.random() * 10}s`
            }}
          />
        ))}
      </div>

      {/* Right side - Menu */}
      <div className="relative z-20 flex flex-col items-center">
        {/* Logo/Title */}
        <div className="text-center mb-8 animate-float">
          <h1 
            className="font-game text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-[#14F195] mb-2 neon-text"
            data-testid="game-title"
          >
            METAL LEGO
          </h1>
          <p className="text-[#9945FF] font-game text-xs sm:text-sm md:text-base">
            SOLANA EDITION
          </p>
        </div>

        {/* Wallet Connection */}
        <div className="mb-6" data-testid="wallet-section">
          {!connected ? (
            <Button
              onClick={() => setVisible(true)}
              className="game-btn px-8 py-3 text-lg font-game flex items-center gap-3 mx-auto"
              style={{ 
                background: 'linear-gradient(135deg, #9945FF 0%, #14F195 100%)',
                border: '2px solid #14F195',
                color: '#fff'
              }}
              data-testid="wallet-connect-btn"
            >
              <Wallet className="w-5 h-5" />
              WALLET CONNECT
            </Button>
          ) : (
            <Button
              onClick={disconnect}
              className="game-btn px-6 py-2 text-sm font-game flex items-center gap-2 mx-auto"
              style={{ 
                background: 'rgba(20, 241, 149, 0.15)',
                border: '2px solid #14F195',
                color: '#14F195'
              }}
              data-testid="wallet-disconnect-btn"
            >
              <Wallet className="w-4 h-4" />
              {publicKey ? `${publicKey.toBase58().slice(0, 4)}...${publicKey.toBase58().slice(-4)}` : 'Connected'}
            </Button>
          )}
          {connected && publicKey && (
            <div className="mt-4 glass-card p-4 text-center">
              <div className="flex items-center justify-center gap-2 text-[#14F195]">
                <Wallet className="w-4 h-4" />
                <span className="text-sm font-mono">
                  {wallet?.adapter?.name || 'Wallet'} Connected
                </span>
              </div>
              <div className="flex items-center justify-center gap-2 mt-2 text-[#FFD700]">
                <Zap className="w-4 h-4" />
                <span className="font-game text-sm" data-testid="token-balance">
                  {tokenBalance.toLocaleString()} SML
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Profile Button */}
        <div className="mb-4 text-center">
          <Button
            onClick={() => onProfile && onProfile()}
            className="text-xs px-4 py-2 flex items-center gap-2 mx-auto"
            style={{ background: 'rgba(153,69,255,0.15)', border: '1px solid #9945FF', color: '#9945FF' }}
            data-testid="profile-btn"
          >
            <User className="w-4 h-4" /> MY PROFILE
          </Button>
        </div>

        {/* Loadout button — replaces the static skin indicator */}
        <button
          onClick={onLoadout}
          data-testid="loadout-btn"
          className="mb-6 group bg-gradient-to-r from-[#0a1a0a] to-[#0a0a14] border border-[#14F195]/30 hover:border-[#14F195] rounded-xl px-5 py-3 flex items-center gap-4 transition-all hover:shadow-[0_0_18px_rgba(20,241,149,0.25)]">
          {/* Skin preview */}
          <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
            style={{ backgroundColor: (selectedSkin?.color || '#FF3B30') + '22', border: `1px solid ${selectedSkin?.color || '#FF3B30'}55` }}>
            <div className="w-5 h-7 rounded" style={{ backgroundColor: selectedSkin?.color || '#FF3B30' }} />
          </div>
          {/* Loadout summary */}
          <div className="text-left">
            <p className="font-game text-base text-white font-bold flex items-center gap-1.5">
              <Backpack className="w-3.5 h-3.5 text-[#14F195]" /> LOADOUT
            </p>
            <div className="flex items-center gap-2 mt-1">
              <span className="text-[10px] text-[#14F195] font-game">
                {selectedWeapon === 'assault_rifle' ? '🎯 Rifle'
                : selectedWeapon === 'rocket_launcher' ? '🚀 Rocket'
                : selectedWeapon === 'machine_gun' || selectedWeapon === 'mini_tank' ? '🔫 MG'
                : 'Default'}
              </span>
              {bringGrenade && (
                <span className="text-[10px] text-[#FF9500] font-game flex items-center gap-1">
                  <Bomb className="w-2.5 h-2.5" /> Grenades
                </span>
              )}
            </div>
          </div>
          {/* Edit hint */}
          <span className="ml-auto text-[10px] text-[#A1A1AA] group-hover:text-[#14F195] font-game">EDIT →</span>
        </button>

        {/* Menu Buttons — compact + translucent so bg video shows through */}
        <div className="flex flex-col gap-2 w-full max-w-xs">
          <Button
            onClick={onPlay}
            className="game-btn w-full text-sm sm:text-base py-3 sm:py-3.5 menu-item backdrop-blur-md"
            style={{ backgroundColor: 'rgba(20, 241, 149, 0.15)' }}
            data-testid="play-btn"
          >
            <Gamepad2 className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
            PLAY
          </Button>

          <Button
            onClick={onShop}
            className="game-btn game-btn-secondary w-full py-2 sm:py-2.5 text-xs sm:text-sm menu-item backdrop-blur-md"
            style={{ backgroundColor: 'rgba(153, 69, 255, 0.15)' }}
            data-testid="shop-btn"
          >
            <ShoppingBag className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2" />
            SHOP
          </Button>

          <Button
            onClick={onLeaderboard}
            className="game-btn game-btn-secondary w-full py-2 sm:py-2.5 text-xs sm:text-sm menu-item backdrop-blur-md"
            style={{ backgroundColor: 'rgba(153, 69, 255, 0.15)' }}
            data-testid="leaderboard-btn"
          >
            <Trophy className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2" />
            LEADERBOARD
          </Button>

          <Button
            onClick={onChallenges}
            className="game-btn w-full py-2 sm:py-2.5 text-xs sm:text-sm menu-item backdrop-blur-md"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.55) 0%, rgba(255, 140, 0, 0.55) 100%)',
              border: '2px solid rgba(255, 215, 0, 0.8)'
            }}
            data-testid="challenges-btn"
          >
            <Calendar className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2" />
            DAILY CHALLENGES
          </Button>

          <Button
            onClick={onMultiplayer}
            className="game-btn w-full py-2 sm:py-2.5 text-xs sm:text-sm menu-item backdrop-blur-md"
            style={{
              background: 'linear-gradient(135deg, rgba(153, 69, 255, 0.55) 0%, rgba(20, 241, 149, 0.55) 100%)',
              border: '2px solid rgba(153, 69, 255, 0.8)'
            }}
            data-testid="multiplayer-btn"
          >
            <Users className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2" />
            MULTIPLAYER
          </Button>

          <Button
            onClick={onTournaments}
            className="game-btn w-full py-2 sm:py-2.5 text-xs sm:text-sm menu-item backdrop-blur-md"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.55) 0%, rgba(255, 149, 0, 0.55) 100%)',
              border: '2px solid rgba(255, 215, 0, 0.8)',
              color: '#000'
            }}
            data-testid="tournaments-btn"
          >
            <Trophy className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2" />
            TOURNAMENTS
          </Button>

          <Button
            onClick={onSpectate}
            className="game-btn w-full py-2 sm:py-2.5 text-xs sm:text-sm menu-item backdrop-blur-md"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 149, 0, 0.55) 0%, rgba(255, 59, 48, 0.55) 100%)',
              border: '2px solid rgba(255, 149, 0, 0.8)',
              color: '#000'
            }}
            data-testid="spectate-btn"
          >
            <Eye className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2" />
            SPECTATE
          </Button>

          <Button
            onClick={onBossRoster}
            className="game-btn w-full py-2 sm:py-2.5 text-xs sm:text-sm menu-item backdrop-blur-md"
            style={{
              background: 'linear-gradient(135deg, rgba(255, 215, 0, 0.55) 0%, rgba(255, 149, 0, 0.55) 100%)',
              border: '2px solid rgba(255, 215, 0, 0.8)',
              color: '#000'
            }}
            data-testid="boss-roster-btn"
          >
            <Crown className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2" />
            BOSS ROSTER
          </Button>

          <Button
            onClick={onBestiary}
            className="game-btn w-full py-2 sm:py-2.5 text-xs sm:text-sm menu-item backdrop-blur-md"
            style={{
              background: 'linear-gradient(135deg, rgba(74, 222, 128, 0.55) 0%, rgba(20, 241, 149, 0.55) 100%)',
              border: '2px solid rgba(74, 222, 128, 0.8)',
              color: '#000'
            }}
            data-testid="bestiary-btn"
          >
            <BookOpen className="w-3.5 h-3.5 sm:w-4 sm:h-4 mr-2" />
            BESTIARY
          </Button>
        </div>

        {/* Connect Wallet Prompt */}
        {!connected && (
          <p className="text-[#A1A1AA] text-sm mt-6 text-center">
            Connect wallet to access shop & save scores
          </p>
        )}
      </div>

      {/* Sound Settings Button + Install App + Notifications (top-right cluster) */}
      <div className="absolute top-4 right-4 z-20 flex items-center gap-2">
        <EnableNotificationsButton />
        <InstallAppButton />
        <button
          onClick={() => setShowSoundSettings(true)}
          className="p-2 rounded-full bg-[#1a1a1a]/80 border border-[#333] hover:border-[#14F195] text-[#A1A1AA] hover:text-[#14F195] transition-colors"
          data-testid="menu-sound-btn"
        >
          <Volume2 className="w-5 h-5" />
        </button>
      </div>

      {/* Backend Status Badge — only visible to wallet-connected users (dev/admin) */}
      {connected && publicKey && <BackendStatusBadge />}

      {/* Footer */}
      <div className="absolute bottom-4 left-0 right-0 text-center z-20">
        <p className="text-[#52525B] text-xs font-game">
          POWERED BY SOLANA
        </p>
      </div>

      {/* Sound Settings Modal */}
      <SoundSettings isOpen={showSoundSettings} onClose={() => setShowSoundSettings(false)} />
    </div>
  );
};

export default MainMenu;
